import React, { useEffect, useState } from "react";
import { db } from "../../firebase";
import {
  collection,
  addDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
} from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Briefcase, MapPin, Users, CalendarDays, FileText } from "lucide-react";

export default function JobManagement() {
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [form, setForm] = useState({
    title: "",
    location: "",
    workExperience: "",
    openings: "",
    workPattern: "",
  });
  const [editJob, setEditJob] = useState(null);
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [globalSearch, setGlobalSearch] = useState("");
  const [selectedJobFilter, setSelectedJobFilter] = useState("all");

  // Fetch Jobs
  const fetchJobs = async () => {
    try {
      const snapshot = await getDocs(collection(db, "Jobs"));
      setJobs(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
    } catch (error) {
      console.error("Error fetching jobs:", error);
    }
  };

  // Fetch Applications
  const fetchApplications = async () => {
    try {
      const snapshot = await getDocs(collection(db, "Applications"));
      const apps = snapshot.docs
        .map((doc) => ({ id: doc.id, ...doc.data() }))
        .sort(
          (a, b) =>
            (b.appliedAt?.seconds || 0) * 1000 - (a.appliedAt?.seconds || 0) * 1000
        );
      setApplications(apps);
    } catch (error) {
      console.error("Error fetching applications:", error);
    }
  };

  useEffect(() => {
    fetchJobs();
    fetchApplications();
  }, []);

  // Add Job
  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      await addDoc(collection(db, "Jobs"), {
        ...form,
        createdAt: new Date(),
      });
      setForm({
        title: "",
        location: "",
        workExperience: "",
        openings: "",
        workPattern: "",
      });
      fetchJobs();
    } catch (error) {
      console.error("Error adding job:", error);
    }
  };

  // Update Job
  const handleUpdate = async (id) => {
    try {
      await updateDoc(doc(db, "Jobs", id), { ...editJob });
      setEditJob(null);
      fetchJobs();
    } catch (error) {
      console.error("Error updating job:", error);
    }
  };

  // Delete Job
  const handleDelete = async (id) => {
    try {
      await deleteDoc(doc(db, "Jobs", id));
      fetchJobs();
    } catch (error) {
      console.error("Error deleting job:", error);
    }
  };

  // Check if application is today
  const isToday = (timestamp) => {
    if (!timestamp?.seconds) return false;
    const appDate = new Date(timestamp.seconds * 1000);
    const today = new Date();
    return (
      appDate.getDate() === today.getDate() &&
      appDate.getMonth() === today.getMonth() &&
      appDate.getFullYear() === today.getFullYear()
    );
  };

  return (
    <div className="p-6 bg-gray-100 min-h-screen">
      <h1 className="text-3xl font-bold mb-6 text-[rgb(32,64,128)]">
        Job Management Dashboard
      </h1>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Jobs */}
        <div>
          {/* Add Job Form */}
          <form
            onSubmit={handleAdd}
            className="grid gap-3 mb-6 bg-white p-5 rounded-xl shadow"
          >
            <h2 className="text-xl font-semibold text-[rgb(32,64,128)]">
              Add New Job
            </h2>
            {["title", "location", "workExperience", "openings", "workPattern"].map(
              (field) => (
                <input
                  key={field}
                  placeholder={
                    field === "workExperience"
                      ? "Experience"
                      : field.charAt(0).toUpperCase() + field.slice(1)
                  }
                  value={form[field]}
                  onChange={(e) => setForm({ ...form, [field]: e.target.value })}
                  className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-2 focus:border-[#204080] focus:ring-0 focus:outline-none sm:text-sm p-2"
                />
              )
            )}
            <Button
              type="submit"
              className="bg-[rgb(32,64,128)] text-white hover:bg-[rgb(59,130,246)] transition-colors duration-300"
            >
              Add Job
            </Button>
          </form>

          {/* Jobs List */}
          <div className="grid gap-4">
            {jobs.map((job) => (
              <div
                key={job.id}
                className="border rounded-xl shadow p-5 bg-white hover:shadow-lg transition"
              >
                {editJob?.id === job.id ? (
                  // Edit Mode
                  <div className="grid gap-2">
                    {["title", "location", "workExperience", "openings", "workPattern"].map(
                      (field) => (
                        <input
                          key={field}
                          value={editJob[field]}
                          onChange={(e) =>
                            setEditJob({ ...editJob, [field]: e.target.value })
                          }
                          className="border-2 border-[rgb(32,64,128)] p-2 rounded focus:outline-none"
                        />
                      )
                    )}
                    <div className="flex gap-2">
                      <Button
                        onClick={() => handleUpdate(job.id)}
                        className="bg-[rgb(32,64,128)] text-white"
                      >
                        Save
                      </Button>
                      <Button
                        onClick={() => setEditJob(null)}
                        variant="outline"
                        className="border-[rgb(32,64,128)] text-[rgb(32,64,128)]"
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : (
                  // View Mode
                  <>
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="text-lg font-semibold text-[rgb(32,64,128)] flex items-center gap-2">
                          <Briefcase size={18} /> {job.title}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <MapPin size={14} /> {job.location}{" "}
                          {job.workExperience && `• ${job.workExperience}`}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <Users size={14} /> Openings: {job.openings || "N/A"}
                        </p>
                        <p className="text-sm text-gray-600 flex items-center gap-2">
                          <CalendarDays size={14} /> {job.workPattern || "N/A"}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          onClick={() => setEditJob(job)}
                          className="bg-[rgb(32,64,128)] text-white hover:bg-[rgb(59,130,246)]"
                        >
                          Edit
                        </Button>
                        <Button
                          onClick={() => handleDelete(job.id)}
                          className="bg-red-600 text-white hover:opacity-90"
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                    <p className="mt-2 text-sm text-gray-500">
                      Applicants:{" "}
                      {
                        applications.filter((app) => app.jobId === job.id)
                          .length
                      }
                    </p>
                  </>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Right Column - Applications */}
<div>
  <div className="bg-white p-5 rounded-xl shadow">
    <h2 className="text-xl font-semibold text-[rgb(32,64,128)] mb-3">
      Recent Applications
    </h2>

    {/* Job Filter Dropdown */}
    <select
      className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-2 focus:border-[#204080] focus:ring-0 focus:outline-none sm:text-sm p-2"
      value={selectedJobFilter}
      onChange={(e) => setSelectedJobFilter(e.target.value)}
    >
      <option value="all">All Jobs</option>
      {jobs.map((job) => (
        <option key={job.id} value={job.id}>
          {job.title}
        </option>
      ))}
    </select>

    {/* Search Input */}
    <input
      type="text"
      placeholder="Search by name or email..."
      value={globalSearch}
      onChange={(e) => setGlobalSearch(e.target.value)}
      className="block w-full rounded-md border border-gray-300 shadow-sm focus:border-2 focus:border-[#204080] focus:ring-0 focus:outline-none sm:text-sm p-2"
    />

    {/* Applications List */}
    <div className="grid gap-3 max-h-[70vh] overflow-y-auto">
      {applications
        .filter(
          (app) =>
            (selectedJobFilter === "all" || app.jobId === selectedJobFilter) &&
            ((app?.name || "").toLowerCase().includes(globalSearch.toLowerCase()) ||
             (app?.email || "").toLowerCase().includes(globalSearch.toLowerCase()))
        )
        .map((app) => (
          <div
            key={app.id}
            onClick={() => setSelectedApplication(app)}
            className={`p-3 border rounded-lg flex justify-between items-center cursor-pointer transition ${
              isToday(app.appliedAt)
                ? "bg-[rgb(59,130,246)]/20"
                : "bg-gray-50"
            } hover:bg-gray-100`}
          >
            <div>
              <p className="font-medium text-[rgb(32,64,128)]">
                {app.name}{" "}
                {isToday(app.appliedAt) && (
                  <span className="text-[rgb(59,130,246)] font-semibold text-xs px-1.5 py-0.5 rounded-full border border-[rgb(59,130,246)] ml-2">
                    Today
                  </span>
                )}
              </p>
              <p className="text-sm text-gray-600">
                {app.email} • {app.phone}
              </p>
            </div>
            {app.resumeUrl && <FileText className="text-[rgb(32,64,128)]" />}
          </div>
        ))}

      {applications.filter(
        (app) =>
          (selectedJobFilter === "all" || app.jobId === selectedJobFilter) &&
          ((app?.name || "").toLowerCase().includes(globalSearch.toLowerCase()) ||
           (app?.email || "").toLowerCase().includes(globalSearch.toLowerCase()))
      ).length === 0 && (
        <p className="text-sm text-gray-500">No applications found</p>
      )}
    </div>
  </div>
</div>

      </div>

      {/* Application Modal */}
      {selectedApplication && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-[32rem] shadow-lg max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold text-[rgb(32,64,128)] mb-4">
              Application Details
            </h2>
            <p>
              <span className="font-semibold">Name:</span> {selectedApplication.name}
            </p>
            <p>
              <span className="font-semibold">Email:</span> {selectedApplication.email}
            </p>
            <p>
              <span className="font-semibold">Phone:</span> {selectedApplication.phone}
            </p>
            <p>
              <span className="font-semibold">Job Title:</span> {selectedApplication.jobTitle}
            </p>
            <p>
              <span className="font-semibold">Work Experience:</span> {selectedApplication.workExperience}
            </p>
            <p>
              <span className="font-semibold">Resume:</span> {selectedApplication.resumeName || "Not uploaded"}
            </p>

            {selectedApplication.resumeUrl && (
              <a href={selectedApplication.resumeUrl} target="_blank" rel="noopener noreferrer">
                <Button className="bg-[rgb(32,64,128)] text-white hover:bg-[rgb(59,130,246)] transition-colors duration-300">
                  Download Resume
                </Button>
              </a>
            )}

            <p className="mt-2">
              <span className="font-semibold">Cover Letter:</span>
              <br />
              {selectedApplication.coverLetter || "No cover letter"}
            </p>

            <p className="mt-2">
              <span className="font-semibold">Submitted At:</span>{" "}
              {selectedApplication.appliedAt
                ? new Date(selectedApplication.appliedAt.seconds * 1000).toLocaleString()
                : "N/A"}
            </p>

            <div className="mt-4 flex justify-end">
              <Button
                onClick={() => setSelectedApplication(null)}
                className="bg-[rgb(32,64,128)] text-white hover:bg-[rgb(59,130,246)] transition-colors duration-300"
              >
                Close
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}